import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import messageBoardService, {
  type MessageBoardPostListResult,
  type MessageBoardPostUrgency,
} from '../../../services/messageBoardService';

export const MESSAGE_BOARD_PAGE_SIZE = 5;

export interface MessageBoardFeed {
  foremanAreas: { id: number; name: string }[];
  defaultAreaId: number | null;
  foremanAreaId: number | '';
  setForemanAreaId: (id: number | '') => void;
  urgency: MessageBoardPostUrgency | '';
  setUrgency: (value: MessageBoardPostUrgency | '') => void;
  dateFrom: string;
  setDateFrom: (value: string) => void;
  dateTo: string;
  setDateTo: (value: string) => void;
  page: number;
  setPage: (page: number) => void;
  resetPage: () => void;
  clearFilters: () => void;
  hasExtraFilters: boolean;
  selectedAreaId: number | null;
  selectedAreaName: string;
  loading: boolean;
  error: string | null;
  setError: (value: string | null) => void;
  result: MessageBoardPostListResult | null;
}

export function useMessageBoardFeed(refreshToken = 0): MessageBoardFeed {
  const { currentUser } = useAuth();
  const foremanAreas = currentUser?.foremanAreas ?? [];
  const defaultAreaId =
    (currentUser as { defaultForemanAreaId?: number } | null)?.defaultForemanAreaId ??
    foremanAreas[0]?.id ??
    null;

  const [foremanAreaId, setForemanAreaId] = useState<number | ''>(defaultAreaId ?? '');
  const [urgency, setUrgency] = useState<MessageBoardPostUrgency | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MessageBoardPostListResult | null>(null);

  useEffect(() => {
    if (foremanAreaId === '' && defaultAreaId != null) {
      setForemanAreaId(defaultAreaId);
    }
  }, [defaultAreaId, foremanAreaId]);

  const selectedAreaId = typeof foremanAreaId === 'number' ? foremanAreaId : null;
  const selectedAreaName =
    foremanAreas.find((a) => a.id === selectedAreaId)?.name ?? 'this foreman area';
  const hasExtraFilters = Boolean(urgency || dateFrom || dateTo);

  useEffect(() => {
    if (selectedAreaId == null) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    messageBoardService
      .list({
        foremanAreaId: selectedAreaId,
        urgency: urgency || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        pageSize: MESSAGE_BOARD_PAGE_SIZE,
      })
      .then((next) => {
        if (cancelled) return;
        setResult(next);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setResult(null);
        setError(err instanceof Error ? err.message : 'Failed to load message board posts');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedAreaId, urgency, dateFrom, dateTo, page, refreshToken]);

  const resetPage = () => setPage(1);

  const clearFilters = () => {
    setForemanAreaId(defaultAreaId ?? foremanAreas[0]?.id ?? '');
    setUrgency('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  return {
    foremanAreas,
    defaultAreaId,
    foremanAreaId,
    setForemanAreaId,
    urgency,
    setUrgency,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    page,
    setPage,
    resetPage,
    clearFilters,
    hasExtraFilters,
    selectedAreaId,
    selectedAreaName,
    loading,
    error,
    setError,
    result,
  };
}
